import { useState, useEffect } from "react";
import { useTitanApp } from "@/hooks/useTitanApp";
import {
  updatePatientPrescription,
  verifyPinForPrescriptionApproval,
  patientPrescriptionBulkUpdate,
} from "@/dispatchers/patientPrescriptionDispatcher";
import config from "@/config";
import { generatePrescriptionImageBase64 } from "@/pages/PrivateService/components/components/generatePrescriptionImage";
import { setPrescriptionImageBase64 } from "@/dispatchers/patientDispatchers";
import {
  getPatientPrescriptionById,
  setPrescriptionImagePreview,
  setShowPrintPreviewLoading,
} from "@/dispatchers/patientPrescriptionDispatcher";
import { getPharmacyContactDetails } from "@/dispatchers/pharmacyServiceDispatchers";

import { IntercomTrack } from "@/data/IntercomTrack";
import { intercomActionPrescriptionText } from "@/data/constantStrings";
// Types and Interfaces
export interface PrescriptionItem {
  id: string;
  medication: string;
  strength?: string;
  medicationType?: string;
  dosage: string;
  quantity: string;
  repeatPrescription: boolean;
  medicationDispensed: boolean;
  contraindications: string[];
  warnings: string[];
}

interface Medication {
  name: string;
  contraindications: string[];
  warnings: string[];
}

// Static Data
export const pharmacies = [
  "UPS Pharmacy - Bedminster",
  "Boots Pharmacy - City Centre",
  "Lloyds Pharmacy - Westbury",
  "Independent Pharmacy - Clifton",
];

export const medications: Medication[] = [
  {
    name: "Aspirin",
    contraindications: [
      "Aspirin/anti-inflammatory induced allergy. Previous or active peptic ulceration, haemophilia or other bleeding disorders, gout.",
      "Severe renal or hepatic impairment.",
      "Severe cardiac insufficiency, Lactation.",
    ],
    warnings: [
      "History of bronchospasm, asthma, uncontrolled hypertension, mild to moderate renal or hepatic impairment, dehydration, G6PD deficiency, Elderly, avoid continuous prolonged use, Pregnancy (avoid at term).",
    ],
  },
  {
    name: "Paracetamol",
    contraindications: ["Severe hepatic impairment"],
    warnings: ["Hepatic impairment", "Alcohol dependence", "Elderly patients"],
  },
  {
    name: "Ibuprofen",
    contraindications: [
      "Hypersensitivity to aspirin or any other NSAID",
      "History of gastrointestinal bleeding",
      "Severe heart failure",
      "Severe liver failure",
      "Severe kidney failure",
    ],
    warnings: [
      "History of gastrointestinal disorders",
      "Cardiac impairment",
      "Hypertension",
      "Elderly patients",
    ],
  },
  {
    name: "Amoxicillin",
    contraindications: [
      "Penicillin hypersensitivity",
      "History of penicillin-associated jaundice/hepatic dysfunction",
    ],
    warnings: [
      "History of allergy",
      "Renal impairment",
      "Maintain adequate hydration",
    ],
  },
];

// Default Data
export const getDefaultPrescriptionItem = (): PrescriptionItem => ({
  id: Date.now().toString(),
  medication: "",
  dosage: "",
  quantity: "",
  repeatPrescription: false,
  medicationDispensed: false,
  contraindications: [],
  warnings: [],
});

export const getInitialPrescriptionItems = (): PrescriptionItem[] => [
  {
    id: "1",
    medication: "Aspirin",
    dosage: "Take 1 tablet once daily",
    quantity: "30 tablets",
    repeatPrescription: false,
    medicationDispensed: false,
    contraindications: [
      "Aspirin/anti-inflammatory induced allergy. Previous or active peptic ulceration, haemophilia or other bleeding disorders, gout.",
      "Severe renal or hepatic impairment.",
      "Severe cardiac insufficiency, Lactation.",
    ],
    warnings: [
      "History of bronchospasm, asthma, uncontrolled hypertension, mild to moderate renal or hepatic impairment, dehydration, G6PD deficiency, Elderly, avoid continuous prolonged use, Pregnancy (avoid at term).",
    ],
  },
];

export const getInitialEmailList = (): string[] => [
  "what@invatech-health.co.uk",
  "example@invatech-health.co.uk",
];

// Custom Hook for Prescription State Management
export const usePrescriptionState = () => {
  const [open, setOpen] = useState(false);
  const [selectedPharmacy, setSelectedPharmacy] = useState("");
  const [emailList, setEmailList] = useState<string[]>(getInitialEmailList());
  const [newEmail, setNewEmail] = useState("");
  const [medicationPopovers, setMedicationPopovers] = useState<
    Record<string, boolean>
  >({});
  const [generatedPrescriptionImage, setGeneratedPrescriptionImage] = useState<
    string | null
  >(null);
  const [isGeneratingImage, setIsGeneratingImage] = useState(false);
  const [showImagePreview, setShowImagePreview] = useState(false);
  const [prescriptionItems, setPrescriptionItems] = useState<
    PrescriptionItem[]
  >(getInitialPrescriptionItems());

  return {
    open,
    setOpen,
    selectedPharmacy,
    setSelectedPharmacy,
    emailList,
    setEmailList,
    newEmail,
    setNewEmail,
    medicationPopovers,
    setMedicationPopovers,
    generatedPrescriptionImage,
    setGeneratedPrescriptionImage,
    isGeneratingImage,
    setIsGeneratingImage,
    showImagePreview,
    setShowImagePreview,
    prescriptionItems,
    setPrescriptionItems,
  };
};

// Business Logic Functions
export const addEmail = (
  newEmail: string,
  emailList: string[],
  setEmailList: (emails: string[]) => void,
  setNewEmail: (email: string) => void,
) => {
  if (newEmail && !emailList.includes(newEmail)) {
    setEmailList([...emailList, newEmail]);
    setNewEmail("");
  }
};

export const removeEmail = (
  emailToRemove: string,
  emailList: string[],
  setEmailList: (emails: string[]) => void,
) => {
  setEmailList(emailList.filter((email) => email !== emailToRemove));
};

export const addPrescriptionItem = (
  prescriptionItems: PrescriptionItem[],
  setPrescriptionItems: (items: PrescriptionItem[]) => void,
) => {
  const newItem = getDefaultPrescriptionItem();
  setPrescriptionItems([...prescriptionItems, newItem]);
};

export const updatePrescriptionItem = (
  id: string,
  updates: Partial<PrescriptionItem>,
  prescriptionItems: PrescriptionItem[],
  setPrescriptionItems: (items: PrescriptionItem[]) => void,
) => {
  setPrescriptionItems(
    prescriptionItems.map((item) =>
      item.id === id ? { ...item, ...updates } : item,
    ),
  );
};

export const updateMedication = (
  id: string,
  medicationName: string,
  prescriptionItems: PrescriptionItem[],
  setPrescriptionItems: (items: PrescriptionItem[]) => void,
) => {
  const medication = medications.find((med) => med.name === medicationName);
  if (medication) {
    updatePrescriptionItem(
      id,
      {
        medication: medicationName,
        contraindications: medication.contraindications,
        warnings: medication.warnings,
      },
      prescriptionItems,
      setPrescriptionItems,
    );
  }
};

// Prescription Image Generation Logic
export const generatePrescriptionImage = async (
  prescriptionItems: PrescriptionItem[],
  setIsGeneratingImage: (loading: boolean) => void,
  setGeneratedPrescriptionImage: (image: string) => void,
): Promise<void> => {
  setIsGeneratingImage(true);

  try {
    // Simulate prescription image generation
    await new Promise((resolve) => setTimeout(resolve, 2000));

    // Generate a mock prescription image
    const canvas = document.createElement("canvas");
    canvas.width = 600;
    canvas.height = 800;
    const ctx = canvas.getContext("2d");

    if (ctx) {
      // Colors
      const lightBlue = "#5DADE2";
      const darkGray = "#2C3E50";
      const mediumGray = "#6C757D";

      // Create prescription design with clean background
      ctx.fillStyle = "#ffffff";
      ctx.fillRect(0, 0, 600, 800);

      // Left side accent line
      ctx.fillStyle = lightBlue;
      ctx.fillRect(0, 0, 8, 800);

      // Header Section
      ctx.fillStyle = darkGray;
      ctx.font = "bold 28px Arial";
      ctx.fillText("Dr. Andrew", 50, 60);
      ctx.font = "28px Arial";
      ctx.fillText("Staton", 200, 60);

      ctx.fillStyle = mediumGray;
      ctx.font = "12px Arial";
      ctx.fillText("QUALIFICATION", 50, 80);

      // Stethoscope icon representation (simple design)
      ctx.strokeStyle = lightBlue;
      ctx.lineWidth = 3;
      ctx.beginPath();
      ctx.arc(500, 45, 15, 0, Math.PI * 2);
      ctx.stroke();
      ctx.beginPath();
      ctx.arc(520, 45, 15, 0, Math.PI * 2);
      ctx.stroke();
      ctx.beginPath();
      ctx.moveTo(515, 45);
      ctx.lineTo(515, 70);
      ctx.stroke();
      ctx.beginPath();
      ctx.arc(515, 75, 5, 0, Math.PI * 2);
      ctx.fill();

      // Patient Information Section
      ctx.fillStyle = darkGray;
      ctx.font = "14px Arial";
      ctx.fillText("Patient Name:", 50, 150);
      ctx.fillText("Date:", 400, 150);

      // Draw underlines for patient info
      ctx.strokeStyle = mediumGray;
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.moveTo(140, 153);
      ctx.lineTo(380, 153);
      ctx.stroke();
      ctx.beginPath();
      ctx.moveTo(440, 153);
      ctx.lineTo(550, 153);
      ctx.stroke();

      ctx.fillText("Age:", 50, 180);
      ctx.fillText("Gender:", 200, 180);
      ctx.fillText("Weight:", 350, 180);

      // Age underline
      ctx.beginPath();
      ctx.moveTo(85, 183);
      ctx.lineTo(180, 183);
      ctx.stroke();
      // Gender underline
      ctx.beginPath();
      ctx.moveTo(245, 183);
      ctx.lineTo(330, 183);
      ctx.stroke();
      // Weight underline
      ctx.beginPath();
      ctx.moveTo(395, 183);
      ctx.lineTo(480, 183);
      ctx.stroke();

      ctx.fillText("Diagnosis:", 50, 210);
      // Diagnosis underline
      ctx.beginPath();
      ctx.moveTo(115, 213);
      ctx.lineTo(550, 213);
      ctx.stroke();

      // Large Rx Symbol
      ctx.fillStyle = lightBlue;
      ctx.font = "bold 120px serif";
      ctx.fillText("R", 50, 320);
      ctx.font = "bold 60px serif";
      ctx.fillText("x", 120, 320);

      // Main prescription area (large empty space for writing)
      ctx.strokeStyle = "#E5E5E5";
      ctx.lineWidth = 1;
      for (let i = 0; i < 15; i++) {
        const y = 250 + i * 25;
        ctx.beginPath();
        ctx.moveTo(200, y);
        ctx.lineTo(550, y);
        ctx.stroke();
      }

      // Prescription items (if any)
      if (prescriptionItems.length > 0) {
        ctx.fillStyle = darkGray;
        ctx.font = "14px Arial";
        let yPos = 270;
        prescriptionItems.forEach((item, index) => {
          if (item.medication) {
            ctx.fillText(`${index + 1}. ${item.medication}`, 210, yPos);
            if (item.dosage) {
              ctx.font = "12px Arial";
              ctx.fillText(`    ${item.dosage}`, 210, yPos + 15);
              ctx.font = "14px Arial";
            }
            if (item.quantity) {
              ctx.font = "12px Arial";
              ctx.fillText(`    Qty: ${item.quantity}`, 210, yPos + 30);
              ctx.font = "14px Arial";
            }
            yPos += 50;
          }
        });
      }

      // Signature Section
      ctx.fillStyle = darkGray;
      ctx.font = "14px Arial";
      ctx.fillText("Signature", 400, 620);

      // Signature line
      ctx.strokeStyle = mediumGray;
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.moveTo(400, 635);
      ctx.lineTo(550, 635);
      ctx.stroke();

      // Footer with contact information
      ctx.fillStyle = mediumGray;
      ctx.font = "10px Arial";

      // Location icon representation
      ctx.fillStyle = darkGray;
      ctx.beginPath();
      ctx.arc(55, 750, 3, 0, Math.PI * 2);
      ctx.fill();

      // Phone icon representation
      ctx.fillRect(200, 748, 6, 4);

      // Email icon representation
      ctx.strokeStyle = darkGray;
      ctx.lineWidth = 1;
      ctx.strokeRect(400, 748, 8, 6);

      ctx.fillStyle = mediumGray;
      ctx.fillText("24 Dummy Street Area", 70, 753);
      ctx.fillText("+12-345 678 9012", 215, 753);
      ctx.fillText("Email ID Space", 415, 753);

      // Convert to base64
      const imageDataUrl = canvas.toDataURL("image/png");
      setGeneratedPrescriptionImage(imageDataUrl);
    }
  } catch (error) {
    console.error("Error generating prescription image:", error);
  } finally {
    setIsGeneratingImage(false);
  }
};

// Save Prescription Logic
export const savePrescription = (
  selectedPharmacy: string,
  emailList: string[],
  prescriptionItems: PrescriptionItem[],
  generatedPrescriptionImage: string,
  onSave?: (prescription: any) => void,
  setOpen?: (open: boolean) => void,
) => {
  const prescription = {
    pharmacy: selectedPharmacy,
    emails: emailList,
    items: prescriptionItems,
    timestamp: new Date(),
    prescriptionImage: generatedPrescriptionImage,
  };

  if (onSave) {
    onSave(prescription);
  }

  if (setOpen) {
    setOpen(false);
  }
};

// Download Prescription Logic
export const downloadPrescription = (prescriptionImage: string) => {
  const link = document.createElement("a");
  link.download = `prescription-${new Date().toISOString().split("T")[0]}.png`;
  link.href = prescriptionImage;
  link.click();
};

// Print Prescription Logic
export const printPrescription = () => {
  window.print();
};

export const usePrescriptionData = () => {
  const { appointmentApp } = config;
  const { appState, nmsServiceState, patientPrescriptionState, patientState } =
    useTitanApp();
  const {
    companyId,
    currentPharmacy,
    currentUser,
    company,
    pharmacies,
    currentPharmacyId,
  } = appState;
  const { darkMode } = appState || {};
  const { isDrugSearch, drugSummary } = nmsServiceState || {};
  const [medications, setMedications] = useState([]);
  const [pharmaciesOptions, setPharmaciesOptions] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");

  const {
    patientPrescriptions,
    userVerification,
    prescriptionImagePreview,
    previewPrescription,
  } = patientPrescriptionState || {};

  const { prescriptionImageBase64 } = patientState || {};

  const getHtmlData = async (data, Drugs, isPreview, prescriptionId = "") => {
    const targetPrescription = patientPrescriptions.find(
      (x) => x.patientPrescription?._id === prescriptionId,
    );
    const contactDetails = await getPharmacyContactDetails(currentPharmacyId);
    const selectedPatientAllergies =
      data?.patient?.allergies?.length > 0
        ? data?.patient?.allergies?.map((a) => a.allergyName).join(", ") + ","
        : null;

    let phoneNumber = "";
    if (
      data?.patient?.contacts &&
      Array.isArray(data.patient.contacts) &&
      data.patient.contacts.length > 0
    ) {
      phoneNumber = data.patient.contacts[0]?.value || "";
    } else if (data?.patient?.phone) {
      // Fallback to direct phone property if available
      phoneNumber = data.patient.phone;
    }

    const pharmacistInfo = {
      createdBy : targetPrescription?.patientPrescription?.createdBy ?? "",
      createdByName : targetPrescription?.patientPrescription?.createdByName ?? ""
    };

    const htmlData = {
      prescriptionDate: new Date().toLocaleDateString(),
      pharmacistRegInfo: "12/04/1987",
      prescriptionLink: appointmentApp,
      patientDetails: {
        title: data?.patient?.title || "",
        name: `${data?.patient?.foreName || ""} ${
          data?.patient?.surName || ""
        }`,
        address: data?.patient?.address || "",
        dob: data?.patient?.dob
          ? new Date(data?.patient?.dob).toLocaleDateString("en-GB")
          : "",
        phone: phoneNumber,
        email: data?.patient?.email,
        allergies: selectedPatientAllergies,
      },
      prescriptionInfo: Drugs,
      paymentInfo: "£21.54",
      authorizationCode: Math.floor(100000 + Math.random() * 900000).toString(),
      pharmacistInstructions: [
        "Visit tvrx.co.uk",
        "Check ePrescription is valid for dispensing by entering the authorisation code",
        "If ePrescription is valid, dispense to the patient and press 'Mark as Dispensed'",
        "If ePrescription is invalid or has expired, do not dispense to patient",
      ],
      pharmacyDetails: currentPharmacy,
      currentUser: currentUser,
      pharmacistInformation: pharmacistInfo,
      company: company,
      pharmacyContactDetails: contactDetails,
      approvedPharmacies: [
        `${currentPharmacy?.teamPharmacyName ?? ""} ${
          currentPharmacy?.teamPharmacyAddress ?? ""
        }`,
      ],
      signature: "",
      prescriptionType: mapPrescriptionCategory(
        data?.patientPrescription?.prescriptionCategory,
      ),
    };
    try {
      const result = await generatePrescriptionImageBase64({ data: htmlData });
      if (!result) {
        console.error("Failed to generate prescription image");
        return;
      }
      const { base64Image } = result;
      setPrescriptionImageBase64(base64Image);
      return result;
    } catch (error) {
      console.error("Error generating preview:", error);
    } finally {
      // setIsPreviewLoading(false);
    }
  };

  const handleUpdatePrescription = async (data: any) => {
    const id = data?.id;
    const result = patientPrescriptions.find(
      (x) => x.patientPrescription?._id === id,
    );

    const Drugs = data.prescriptionItems.map((item: any) => ({
      PrescriptionItemId: item.PrescriptionItemId || item.prescriptionItemId,
      drugName: item.medication || item.drugName,
      quantity: item.quantity ? parseFloat(item.quantity) : 0,
      dose: item.dosage || item.dose,
      note: item.notes || item.note,
    }));
    const prescriptionImage = await getHtmlData(result, Drugs, false);
    const { base64Image } = prescriptionImage;
    const exsistingPharmacy = pharmacies.find(
      (p) => p.teamPharmacyName === data.pharmacy,
    );
    const payload = {
      _id: data.id,
      drugs: Drugs,
      base64Image: base64Image,
      prescriptionCategory:
        data?.prescriptionCategory === "private"
          ? 1
          : data?.prescriptionCategory === "pgd"
            ? 2
            : 3,
      pharmacyID: exsistingPharmacy?.teamPharmacyID || "",
    };

    const updatePrompt = await updatePatientPrescription(payload);
    await IntercomTrack(
      currentUser,
      currentPharmacy,
      company,
      intercomActionPrescriptionText,
    );
    return updatePrompt;
  };
  const handlePatientPrescriptionBulkUpdate = async (data: any) => {
    const exsistingPharmacy = pharmacies.find(
      (p) => p.teamPharmacyName === data.selectedPharmacy,
    );
    const payload = {
      ids: [...data.prescriptionIds],
      pharmacyID: exsistingPharmacy?.teamPharmacyID,
    };

    const updatePrompt = await patientPrescriptionBulkUpdate(payload);
    return updatePrompt;
  };

  const handleVerifyPinForPrescriptionApproval = async (Pin: any) => {
    const result = await verifyPinForPrescriptionApproval(Pin);
    return result;
  };

  const handleShowPrescription = async (
    prescriptionId: string,
    updatedPrescriptionData?: any,
  ) => {
    setShowPrintPreviewLoading(true);
    setPrescriptionImagePreview(" ");

    let patientData;

    const id = prescriptionId;
    patientData = patientPrescriptions.find(
      (x) => x.patientPrescription?._id === id,
    );

    if (updatedPrescriptionData) {
      const prescriptionImage = await getHtmlData(
        patientData,
        updatedPrescriptionData?.medications,
        false,
        prescriptionId
      );
      setPrescriptionImagePreview(prescriptionImage?.base64Image || "");
    } else {
      const prescriptionImage = await getHtmlData(
        patientData,
        patientData.patientPrescription?.drugs,
        false,
        prescriptionId
      );
      setPrescriptionImagePreview(prescriptionImage?.base64Image || "");
    }

    setShowPrintPreviewLoading(false);
  };

  useEffect(() => {
    if (drugSummary && drugSummary.length > 0) {
      const mappedMedications = drugSummary.map((drug) => ({
        name: drug.drugName,
        contraindications: [
          "Aspirin/anti-inflammatory induced allergy.",
          "Severe renal or hepatic impairment.",
          "Severe cardiac insufficiency, Lactation.",
        ],
        warnings: [
          "History of bronchospasm, asthma, uncontrolled hypertension, mild to moderate renal or hepatic impairment, dehydration, G6PD deficiency, Elderly, avoid continuous prolonged use, Pregnancy (avoid at term).",
        ],
      }));
      setMedications(mappedMedications);
    }
  }, [drugSummary]);

  useEffect(() => {
    const mappedPharmacyOptions =
      pharmacies?.map((pharmacy) => pharmacy.teamPharmacyName) || [];
    setPharmaciesOptions(mappedPharmacyOptions);
  }, [pharmacies]);

  const mapPrescriptionCategory = (category: number) => {
    switch (category) {
      case 1:
        return "private";
      case 2:
        return "pgd";
      case 3:
        return "pharmacy-first";
      default:
        return "private";
    }
  };

  return {
    medications,
    darkMode,
    setSearchTerm,
    searchTerm,
    handleUpdatePrescription,
    handleVerifyPinForPrescriptionApproval,
    pharmaciesOptions,
    userVerification,
    handlePatientPrescriptionBulkUpdate,
    handleShowPrescription,
    patientPrescriptions,
    prescriptionImagePreview,
    previewPrescription,
    getHtmlData,
  };
};
